/**
 * The Planner's "Brush up" link, read and written. Pure (KTD4, KTD9).
 *
 * The Planner opens `https://ahead.labs.240.org/from-planner#<fragment>`, where the fragment
 * is URL-encoded form data (`URLSearchParams`, `%20` or `+` for a space):
 *
 * | key       | count  | value                                                                  |
 * |-----------|--------|------------------------------------------------------------------------|
 * | `v`       | 1      | `1` — the format version. Any other value refuses the whole hand-off.  |
 * | `text`    | 1      | The ask text: 1 to `MAX_ASK_LENGTH` (4,000) characters, not blank.     |
 * | `state`   | 1      | One of `JURISDICTIONS`, exactly as spelled there (`New York`).         |
 * | `grade`   | 0..n   | One of `GRADE_LEVELS` each (`K`, `6`); repeat the key for several.     |
 * | `subject` | 0..1   | One of `ACADEMIC_SUBJECTS` (`Mathematics`).                            |
 * | `from`    | 1      | `planner`.                                                             |
 *
 * Example: `#v=1&text=6.RP.A.1%20Understand%20ratios.&state=Ohio&grade=6&subject=Mathematics&from=planner`
 *
 * Any key not listed is ignored, so the Planner may add one without breaking this side. Any
 * listed key that is missing, repeated, or outside its vocabulary refuses the whole hand-off
 * and the form opens as it does today — a half-applied prefill would look deliberate.
 *
 * The text arrives in a fragment rather than a query string because a fragment never leaves
 * the browser: not to the Worker's logs, not to a referrer, not to PostHog's `$current_url`.
 */
import { MAX_ASK_LENGTH } from './ask';
import { decideProfile, type Profile } from './profile';

export const PLANNER_HANDOFF_PATH = '/from-planner';
export const PLANNER_HANDOFF_VERSION = '1';
/** The `from` value a Planner hand-off carries, in its fragment and on its events (KTD7). */
export const PLANNER_SOURCE = 'planner';

export interface PlannerHandoff {
  text: string;
  profile: Profile;
}

/** Longer than any valid fragment: 4,000 characters at nine bytes each once percent-encoded. */
const MAX_FRAGMENT_LENGTH = MAX_ASK_LENGTH * 9 + 1000;

/** The hand-off a fragment carries, or null. Accepts the fragment with or without its `#`. */
export function parsePlannerHandoff(fragment: string): PlannerHandoff | null {
  const raw = fragment.startsWith('#') ? fragment.slice(1) : fragment;
  if (!raw || raw.length > MAX_FRAGMENT_LENGTH) return null;
  const params = new URLSearchParams(raw);
  const one = (key: string): string | null => {
    const values = params.getAll(key);
    return values.length === 1 ? values[0]! : null;
  };
  if (one('v') !== PLANNER_HANDOFF_VERSION || one('from') !== PLANNER_SOURCE) return null;

  const text = one('text');
  if (text === null || text.length > MAX_ASK_LENGTH || !text.trim()) return null;

  const subjects = params.getAll('subject');
  if (subjects.length > 1) return null;
  const decided = decideProfile({ jurisdiction: one('state'), academicSubject: subjects[0], gradeLevels: params.getAll('grade') });
  if (!decided.ok) return null;

  return { text: text.trim(), profile: decided.profile };
}

/** The fragment, `#` included, for a hand-off. The Planner builds the same shape. */
export function plannerHandoffFragment({ text, profile }: PlannerHandoff): string {
  const pairs: [string, string][] = [['v', PLANNER_HANDOFF_VERSION], ['text', text], ['state', profile.jurisdiction]];
  for (const grade of profile.gradeLevels) pairs.push(['grade', grade]);
  if (profile.academicSubject) pairs.push(['subject', profile.academicSubject]);
  pairs.push(['from', PLANNER_SOURCE]);
  return `#${pairs.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&')}`;
}
